import { GridTable, type GridColumn } from "@/shared/components/ds";
import type { TableCell, TableHeader, TableStyle } from "../models/dashboard";
import pageStyles from "../opLaunchPages.module.css";

interface Props {
    header?: TableHeader[] | null;
    /** Rows of cells: HTML strings where the old platform rendered markup, raw numbers elsewhere. */
    data?: TableCell[][] | null;
    tableStyles?: (TableStyle | null)[] | null;
    sort: string;
    onSort: (sort: string) => void;
    maxHeight?: number | string;
    minWidth?: number | string;
}

/** Numeric-looking after the markup, currency, grouping and percent signs come off. */
const NUMERIC = /^[-+]?\$?\s*[\d,]*\.?\d+\s*%?$/;
/** A cell as the HTML to render: OP's templates stringified numbers and blanked nulls the same way. */
const cellHtml = (cell: TableCell) => (cell == null ? "" : String(cell));
const stripTags = (cell: TableCell) => cellHtml(cell).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

function alignOf(tdClass: string | null | undefined, cells: TableCell[]): GridColumn["align"] {
    if (tdClass?.includes("text-right") || tdClass?.includes("text-end")) return "right";
    if (tdClass?.includes("text-center")) return "center";
    const values = cells.map(stripTags).filter((v) => v && v !== "—" && v !== "-");
    return values.length > 0 && values.every((v) => NUMERIC.test(v)) ? "right" : "left";
}

/**
 * The OP dashboard's table envelope (MPN-2230) on the platform's grid table. A header with a
 * sort_key is clickable and toggles between `key` and `-key`; the tile re-requests with that
 * sort. Cells are the server's HTML, trusted because it comes from the frozen OP warehouse,
 * never from a user; the old page's td classes still set the alignment.
 */
export function LegacyTable({ header, data, tableStyles, sort, onSort, maxHeight, minWidth }: Props) {
    if (!header?.length || !data?.length) {
        return <p style={{ margin: 0, padding: "14px", fontSize: "var(--fs-cell)", color: "var(--slate-500)" }}>No data to display.</p>;
    }

    const columns: GridColumn[] = header.map((h, i) => ({
        label: h.caption ?? "",
        sortKey: h.sort_key || undefined,
        align: alignOf(tableStyles?.[i]?.td_class, data.map((row) => row[i])),
        tone: i === 0 ? "strong" : "body",
        track: i === 0 ? "minmax(160px, 1.6fr)" : "minmax(90px, 1fr)",
    }));

    const rows = data.map((row) =>
        columns.map((_, c) => (
            <span className={`${pageStyles.html} ${tableStyles?.[c]?.td_class ?? ""}`.trim()}
                dangerouslySetInnerHTML={{ __html: cellHtml(row[c]) }} />
        )),
    );

    return <GridTable columns={columns} rows={rows} sort={sort} onSort={onSort} maxHeight={maxHeight} minWidth={minWidth} />;
}
